import dayjs from 'dayjs';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { createWithEqualityFn as create } from 'zustand/traditional';
import type { Stages } from '../../api/process-client/process-client.type';
import type { TaskType } from '../../containers/tasks-table/tasks-table.type';
import { isLoggingEnabled, logger } from '../logger/logger';
import type { CachedProcessScope, EventScope, ProcessScope, ProcessWorkflow } from './process-store.type';

export interface ProcessStore {
  // Left (primary) section state
  scope: ProcessScope;
  setScope: (newScope: Partial<ProcessScope>) => void;
  stages: Stages[];
  setStages: (stages: Stages[]) => void;
  workflow: ProcessWorkflow | null;
  setWorkflow: (workflow: ProcessWorkflow | null) => void;
  eventScope: EventScope;
  setEventScope: (eventScope: EventScope) => void;

  // Comparison view properties
  isComparisonView: boolean;
  setIsComparisonView: (isComparisonView: boolean) => void;
  rightScope: ProcessScope;
  setRightScope: (newScope: Partial<ProcessScope>) => void;
  rightStages: Stages[];
  setRightStages: (stages: Stages[]) => void;
  rightWorkflow: ProcessWorkflow | null;
  setRightWorkflow: (workflow: ProcessWorkflow | null) => void;
  rightEventScope: EventScope;
  setRightEventScope: (eventScope: EventScope) => void;
}

const defaultCachedScope: CachedProcessScope = {
  portfolio: null,
  team: null,
  board: null,
  epic: null,
  task: null,
  workflow: null,
};

const defaultEventScope: EventScope = {
  showEventGraph: false,
  statuses: [],
  statusGroup: null,
  selectedEdgeId: null,
};

// Get the cached scope from localStorage or use default values
const getCachedScope = (): CachedProcessScope => {
  try {
    const storedScope = localStorage.getItem('process-scope');
    return storedScope ? JSON.parse(storedScope) : defaultCachedScope;
  } catch (error) {
    console.error('Error parsing cached process scope:', error);
    return defaultCachedScope;
  }
};

const useProcessStoreUpdate = create<ProcessStore>()(
  devtools(
    persist(
      logger((set) => {
        const cachedScope = getCachedScope();

        return {
          // Left (primary) section state
          scope: {
            ...cachedScope,
            startDate: dayjs().subtract(1, 'month').startOf('month').toISOString(),
            endDate: dayjs().toISOString(),
            taskType: null,
            useWorkflow: false,
            useGroups: true,
          },
          setScope: (newScope: Partial<ProcessScope>) => {
            set(
              (state) => {
                let updatedCachedScope = { ...state.scope };
                let hasChanged = false;

                // Handle portfolio changes
                if ('portfolio' in newScope) {
                  updatedCachedScope = {
                    ...updatedCachedScope,
                    portfolio: newScope.portfolio === undefined ? null : newScope.portfolio,
                    team: null,
                    board: null,
                    epic: null,
                    task: null,
                  };
                  hasChanged = true;
                }

                // Handle team changes (including setting to null)
                if ('team' in newScope) {
                  const newTeam = newScope.team === undefined ? null : newScope.team;
                  updatedCachedScope = {
                    ...updatedCachedScope,
                    team: newTeam,
                    // Clear dependent fields when team is null
                    board: newTeam ? updatedCachedScope.board : null,
                    epic: newTeam ? updatedCachedScope.epic : null,
                    task: newTeam ? updatedCachedScope.task : null,
                  };
                  hasChanged = true;
                }

                // Handle board changes (including setting to null)
                if ('board' in newScope) {
                  const newBoard = newScope.board === undefined ? null : newScope.board;
                  updatedCachedScope = {
                    ...updatedCachedScope,
                    board: newBoard,
                    // Clear epic and task when board is null
                    epic: newBoard ? updatedCachedScope.epic : null,
                    task: newBoard ? updatedCachedScope.task : null,
                  };
                  hasChanged = true;
                }

                // Handle epic changes (including setting to null)
                if ('epic' in newScope) {
                  const newEpic = newScope.epic === undefined ? null : newScope.epic;
                  updatedCachedScope = {
                    ...updatedCachedScope,
                    epic: newEpic,
                    // Clear task when epic changes
                    task: null,
                  };
                  hasChanged = true;
                }

                // Handle task changes (including setting to null)
                if ('task' in newScope) {
                  const newTask = newScope.task === undefined ? null : newScope.task;
                  updatedCachedScope = {
                    ...updatedCachedScope,
                    task: newTask,
                    // Clear epic when task is null
                    epic: newTask ? updatedCachedScope.epic : null,
                  };
                  hasChanged = true;
                }

                // Handle workflow changes
                if ('workflow' in newScope) {
                  const newWorkflow = newScope.workflow === undefined ? null : newScope.workflow;
                  updatedCachedScope = {
                    ...updatedCachedScope,
                    workflow: newWorkflow,
                  };
                  hasChanged = true;
                }

                // Handle taskType changes - clear task when taskType changes
                if ('taskType' in newScope) {
                  updatedCachedScope = {
                    ...updatedCachedScope,
                    task: null,
                  };
                  hasChanged = true;
                }

                // Store cached scope in localStorage
                if (hasChanged) {
                  const cachedScopeToStore: CachedProcessScope = {
                    portfolio: updatedCachedScope.portfolio,
                    team: updatedCachedScope.team,
                    board: updatedCachedScope.board,
                    epic: updatedCachedScope.epic,
                    task: updatedCachedScope.task,
                    workflow: updatedCachedScope.workflow,
                  };
                  localStorage.setItem('process-scope', JSON.stringify(cachedScopeToStore));
                }

                // Handle other fields
                return {
                  scope: {
                    ...updatedCachedScope,
                    startDate: newScope.startDate !== undefined ? newScope.startDate : state.scope.startDate,
                    endDate: newScope.endDate !== undefined ? newScope.endDate : state.scope.endDate,
                    taskType: newScope.taskType !== undefined ? newScope.taskType : state.scope.taskType,
                    useWorkflow: newScope.useWorkflow !== undefined ? newScope.useWorkflow : state.scope.useWorkflow,
                    useGroups: newScope.useGroups !== undefined ? !!newScope.useGroups : state.scope.useGroups,
                  },
                };
              },
              false,
              '[Process Store] setScope',
            );
          },
          stages: [],
          setStages: (stages: Stages[]) => set({ stages }, false, '[Process Store] setStages'),
          workflow: null,
          setWorkflow: (workflow: ProcessWorkflow | null) => set({ workflow }, false, '[Process Store] setWorkflow'),
          eventScope: defaultEventScope,
          setEventScope: (eventScope: EventScope) => set({ eventScope }, false, '[Process Store] setEventScope'),

          // Comparison view properties
          isComparisonView: false,
          setIsComparisonView: (isComparisonView: boolean) =>
            set({ isComparisonView }, false, '[Process Store] setIsComparisonView'),
          rightScope: {
            ...defaultCachedScope,
            startDate: dayjs().subtract(1, 'month').startOf('month').toISOString(),
            endDate: dayjs().toISOString(),
            taskType: null as TaskType | null,
            useWorkflow: false,
            useGroups: true,
          },
          setRightScope: (newScope: Partial<ProcessScope>) => {
            set(
              (state) => {
                let updatedRightScope = { ...state.rightScope };

                // Handle portfolio changes
                if ('portfolio' in newScope) {
                  updatedRightScope = {
                    ...updatedRightScope,
                    portfolio: newScope.portfolio === undefined ? null : newScope.portfolio,
                    team: null,
                    board: null,
                    epic: null,
                    task: null,
                  };
                }

                // Handle team changes (including setting to null)
                if ('team' in newScope) {
                  const newTeam = newScope.team === undefined ? null : newScope.team;
                  updatedRightScope = {
                    ...updatedRightScope,
                    team: newTeam,
                    // Clear dependent fields when team is null
                    board: newTeam ? updatedRightScope.board : null,
                    epic: newTeam ? updatedRightScope.epic : null,
                    task: newTeam ? updatedRightScope.task : null,
                  };
                }

                // Handle board changes (including setting to null)
                if ('board' in newScope) {
                  const newBoard = newScope.board === undefined ? null : newScope.board;
                  updatedRightScope = {
                    ...updatedRightScope,
                    board: newBoard,
                    // Clear epic and task when board is null
                    epic: newBoard ? updatedRightScope.epic : null,
                    task: newBoard ? updatedRightScope.task : null,
                  };
                }

                // Handle epic changes (including setting to null)
                if ('epic' in newScope) {
                  const newEpic = newScope.epic === undefined ? null : newScope.epic;
                  updatedRightScope = {
                    ...updatedRightScope,
                    epic: newEpic,
                    // Clear task when epic changes
                    task: null,
                  };
                }

                // Handle task changes (including setting to null)
                if ('task' in newScope) {
                  const newTask = newScope.task === undefined ? null : newScope.task;
                  updatedRightScope = {
                    ...updatedRightScope,
                    task: newTask,
                    // Clear epic when task is null
                    epic: newTask ? updatedRightScope.epic : null,
                  };
                }

                // Handle workflow changes
                if ('workflow' in newScope) {
                  const newWorkflow = newScope.workflow === undefined ? null : newScope.workflow;
                  updatedRightScope = {
                    ...updatedRightScope,
                    workflow: newWorkflow,
                  };
                }

                // Handle taskType changes - clear task when taskType changes
                if ('taskType' in newScope) {
                  updatedRightScope = {
                    ...updatedRightScope,
                    task: null,
                  };
                }

                // Handle other fields
                return {
                  rightScope: {
                    ...updatedRightScope,
                    startDate: newScope.startDate !== undefined ? newScope.startDate : state.rightScope.startDate,
                    endDate: newScope.endDate !== undefined ? newScope.endDate : state.rightScope.endDate,
                    taskType: newScope.taskType !== undefined ? newScope.taskType : state.rightScope.taskType,
                    useWorkflow:
                      newScope.useWorkflow !== undefined ? newScope.useWorkflow : state.rightScope.useWorkflow,
                    useGroups: newScope.useGroups !== undefined ? !!newScope.useGroups : state.rightScope.useGroups,
                  },
                };
              },
              false,
              '[Process Store] setRightScope',
            );
          },
          rightStages: [],
          setRightStages: (rightStages: Stages[]) => set({ rightStages }, false, '[Process Store] setRightStages'),
          rightWorkflow: null,
          setRightWorkflow: (rightWorkflow: ProcessWorkflow | null) =>
            set({ rightWorkflow }, false, '[Process Store] setRightWorkflow'),
          rightEventScope: defaultEventScope,
          setRightEventScope: (rightEventScope: EventScope) =>
            set({ rightEventScope }, false, '[Process Store] setRightEventScope'),
        };
      }),
      {
        name: 'process-storage',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          scope: state.scope,
          stages: state.stages,
          workflow: state.workflow,
          eventScope: state.eventScope,
          isComparisonView: state.isComparisonView,
          rightScope: state.rightScope,
          rightStages: state.rightStages,
          rightWorkflow: state.rightWorkflow,
          rightEventScope: state.rightEventScope,
        }),
      },
    ),
    {
      name: 'Bloomfilter - Process Store',
      enabled: isLoggingEnabled,
    },
  ),
);

export { useProcessStoreUpdate };
